import { Component } from "@theme/component";

/**
 * Shop the look detail layout: keeps the product panels on the left in sync
 * with the active slide carousel on the right.
 *
 * Uses a lightweight slide switcher (no Swiper) so adapt-height media keeps
 * its intrinsic size — the same markup path that already works for single-slide looks.
 *
 * Panels other than the first are server-rendered inside a <template> so the
 * browser skips their DOM, image requests and component upgrades until the
 * visitor actually navigates to that look; `#hydratePanel` moves the template
 * content into the panel on first use.
 *
 * Refs:
 * - panels[]: one product grid per slide, matched via data-index
 * - prev, next, pagination: carousel controls when the look has multiple slides
 */
class ShopTheLookDetail extends Component {
  #currentIndex = 0;

  /** @type {number} */
  #slideCount = 0;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#init();
    this.#wireHotspotLinking();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController.abort();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#init();
  }

  /**
   * Two-way link between the image hotspots and the product carousel:
   * clicking a hotspot brings its product into view, and the active product
   * highlights its hotspot. Matched by product handle. No-ops on the metaobject
   * detail page, which has no <shop-the-look-products> carousel.
   */
  #wireHotspotLinking() {
    const { signal } = this.#abortController;

    this.addEventListener(
      "click",
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest("a")) return; // let popover links navigate
        const hotspot = target.closest(".shop-the-looks__hotspot[data-product-handle]");
        if (!(hotspot instanceof HTMLElement)) return;
        const { productHandle } = hotspot.dataset;
        this.#activeProducts()?.scrollToHandle?.(productHandle);
        this.#linkCard(productHandle);
        this.#highlightHotspot(productHandle);
      },
      { signal }
    );

    this.addEventListener(
      "shop-the-look:productchange",
      (event) => {
        this.#highlightHotspot(/** @type {CustomEvent} */ (event).detail?.handle);
      },
      { signal }
    );
  }

  /**
   * Detail page: the active panel is a plain product grid (no carousel), so
   * a hotspot click scrolls the matching product card into view and marks it.
   *
   * @param {string | undefined} handle
   */
  #linkCard(handle) {
    const { panels } = this.refs;
    const active = panels?.find((panel) => !panel.hidden);
    if (!active) return;

    const cards = active.querySelectorAll("product-card[data-product-handle]");
    let target = null;
    for (const card of cards) {
      const isMatch = /** @type {HTMLElement} */ (card).dataset.productHandle === handle;
      card.classList.toggle("is-linked", isMatch);
      if (isMatch) target = card;
    }
    if (!target) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Below 1024px the image sits above the products: bring the card to the middle of the screen.
    const stacked = window.matchMedia("(max-width: 1023px)").matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: stacked ? "center" : "nearest",
      inline: "nearest",
    });
  }

  /** @returns {(HTMLElement & { scrollToHandle?: (handle: string) => void }) | null} */
  #activeProducts() {
    const { panels } = this.refs;
    const active = panels?.find((panel) => !panel.hidden);
    return active?.querySelector("shop-the-look-products") ?? null;
  }

  /**
   * @param {string | undefined} handle
   */
  #highlightHotspot(handle) {
    const slide =
      this.querySelector(".shop-the-look-detail__carousel .shop-the-looks__slide:not([hidden])") ||
      this.querySelector(".shop-the-looks__slide");
    if (!slide) return;

    for (const hotspot of slide.querySelectorAll(".shop-the-looks__hotspot[data-product-handle]")) {
      hotspot.classList.toggle("is-linked", /** @type {HTMLElement} */ (hotspot).dataset.productHandle === handle);
    }
  }

  #init() {
    const slides = this.querySelectorAll(".shop-the-look-detail__carousel .shop-the-looks__slide");
    this.#slideCount = slides.length;

    if (this.#slideCount === 0) return;

    this.#currentIndex = 0;
    this.#showSlide(this.#currentIndex);
    this.#setActivePanel(this.#currentIndex);
    this.#updatePagination();
  }

  previousSlide() {
    if (this.#slideCount <= 1) return;
    this.#goToSlide(this.#currentIndex - 1);
  }

  nextSlide() {
    if (this.#slideCount <= 1) return;
    this.#goToSlide(this.#currentIndex + 1);
  }

  /**
   * @param {Event} event
   */
  goToSlide(event) {
    const target = event.target.closest("[data-index]");
    if (!(target instanceof HTMLElement)) return;

    const index = Number(target.dataset.index);
    if (Number.isNaN(index)) return;

    this.#goToSlide(index);
  }

  /**
   * @param {number} index
   */
  #goToSlide(index) {
    if (this.#slideCount <= 1) return;

    const nextIndex = ((index % this.#slideCount) + this.#slideCount) % this.#slideCount;
    this.#currentIndex = nextIndex;
    this.#showSlide(nextIndex);
    this.#setActivePanel(nextIndex);
    this.#updatePagination();
    this.#linkCard(undefined);
  }

  /**
   * @param {number} index
   */
  #showSlide(index) {
    const slides = this.querySelectorAll(".shop-the-look-detail__carousel .shop-the-looks__slide");

    for (const slide of slides) {
      slide.hidden = Number(slide.dataset.index) !== index;
    }
  }

  /**
   * @param {number} index
   */
  #setActivePanel(index) {
    const { panels } = this.refs;
    if (!panels) return;

    for (const panel of panels) {
      const isActive = Number(panel.dataset.index) === index;
      if (isActive) this.#hydratePanel(panel);
      panel.hidden = !isActive;
    }
  }

  /**
   * Moves the lazily rendered panel content out of its <template> into the
   * panel. No-op once hydrated. Refs of this component are refreshed by the
   * base class's mutation observer; the nested <shop-the-look-products>
   * upgrades on insertion.
   *
   * @param {HTMLElement} panel
   */
  #hydratePanel(panel) {
    const template = panel.querySelector(":scope > template[data-panel-content]");
    if (!(template instanceof HTMLTemplateElement)) return;
    panel.appendChild(template.content);
    template.remove();
  }

  #updatePagination() {
    const { pagination } = this.refs;
    if (!pagination) return;

    const bullets = pagination.querySelectorAll("[data-index]");
    for (const bullet of bullets) {
      bullet.classList.toggle("swiper-pagination-bullet-active", Number(bullet.dataset.index) === this.#currentIndex);
      bullet.setAttribute("aria-current", Number(bullet.dataset.index) === this.#currentIndex ? "true" : "false");
    }
  }
}

if (!customElements.get("shop-the-look-detail")) {
  customElements.define("shop-the-look-detail", ShopTheLookDetail);
}
